import type {
  AdminDeadlineOverrideDto,
  ApiResponseDto,
  AssessmentCycle,
  AssessmentResponse,
  AssessmentStatus,
  BootstrapInstructionTopicsData,
  ClearUserCacheData,
  GetAllOrgsData,
  CompleteUploadData,
  CreateAssessmentCycleDto,
  CreateFirstAidLogDto,
  CreateKomboIntegrationData,
  CreateKomboIntegrationDto,
  CreateLegalEntityData,
  CreateLegalEntityDto,
  CreateMaternityNotificationDto,
  CreateMaternityRiskAssessmentDto,
  CreatePreventionCompletionDto,
  CreatePreventionRuleDto,
  CreatePreventionWithProfileDto,
  CreatePreventionWithProfileResponseDto,
  CreateInstructionWithProfileDto,
  CreateInstructionWithProfileResponseDto,
  CreateUnassignedFirstAidLogDto,
  CreateWorkLocationData,
  CreateWorkLocationDto,
  DeleteDocumentData,
  DocumentDto,
  Employee,
  EnrichedActivityLog,
  EntityTypeEnum,
  FirstAidLog,
  GetAllLogsData,
  GetAllLogsParams,
  GetAssessmentStatisticsData,
  GetAssessmentTimelineParams,
  GetDocumentsParams,
  GetEmployeeMaternityStatusData,
  GetEmployeesWithPreventionStatusData,
  GetKomboIntegrationData,
  GetLogsByEmployeeData,
  GetNotificationCountsData,
  GetOhsHubOrgsData,
  GetOrgBudgetsData,
  GetOrganizationOptionsData,
  GetOrganizationOptionsKomboData,
  GetPreventionDetailLogsParams,
  GetQuestionnaireResponseData,
  Group,
  InstructionStatisticsResponseDto,
  InstructionAssignmentResponseDto,
  InstructionTopicAdminResponseDto,
  UpdateExpertCalendlyTokenDto,
  CreateExpertDto,
  ListAssessmentsParams,
  ListNotificationsParams,
  LocaleEnum,
  ManualCreateEmployeeDto,
  MaternityNotificationResponseDto,
  MaternityRiskAssessmentResponseDto,
  Organization,
  PreventionAssignment,
  PreventionCompletion,
  PreventionProfile,
  ProcessAllPreventionEmailsUnifiedData,
  ProcessAllPreventionEmailsUnifiedParams,
  ProcessMandatoryEmailsForSingleOrgData,
  ProcessMandatoryEmailsForSingleOrgParams,
  ProcessNoticesForAllOrgsData,
  ProcessNoticesForAllOrgsParams,
  ProcessNoticesForSingleOrgData,
  ProcessNoticesForSingleOrgParams,
  QuestionnaireResponseDto,
  ReceiptSummaryResponseDto,
  RequestUploadDto,
  RequestUploadResponseDto,
  SearchUsersData,
  SearchUsersParams,
  SessionMessage,
  SessionMessageDto,
  SubmitAssessmentDto,
  SubmitQuestionnaireData,
  SubmitQuestionnaireResponseDto,
  SurveyResponseDto,
  UpdateAssessmentCycleNoteDto,
  UpdateEmployeeNoteDto,
  UpdateFirstAidLogDto,
  UpdateMaternityNotificationDto,
  UpdateMaternityRiskAssessmentDto,
  UpdateOrganizationData,
  UpdateOrgAccessDto,
  UpdateOrgEapAccessDto,
  UpdateEapAccessData,
  UpdateOrgOhsHubAccessDto,
  UpdateOhsHubAccessData,
  UpdatePreventionAssignmentDeadlineDto,
  UpdatePreventionCompletionDto,
  UpdatePreventionRuleDto,
  UpdateQuestionnaireResponseDto,
  UpdateInstructionsAccessData,
  UpdateUserAccessDto,
  UpdateUserOrganizationDto,
  User,
} from '@/types/Api';
import { Api, CreateKomboIntegrationDtoLanguageEnum } from '@/types/Api';
import type { NormalizedError } from '@/types/NormalizedError';
import { toAppError } from './toAppError';
import { ApiError } from './ApiError';
import { getApiBaseUrl } from './apiUrl';

// Initialize the Swagger API client
export const apiClient = new Api<string>({
  baseUrl: getApiBaseUrl(),
  baseApiParams: {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  },
  securityWorker: (token: string | null) => {
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  },
});

export interface ApiResponse<T> {
  data?: T;
  error?: NormalizedError;
}

/**
 * A generic API wrapper that:
 * - Calls the Swagger client method.
 * - Parses JSON responses.
 * - Automatically logs & returns errors instead of throwing them.
 */
export async function callApi<T, P extends unknown[] = []>(
  apiCall: (...args: P) => Promise<Response>,
  token: string | null,
  ...args: P
): Promise<ApiResponse<T>> {
  // Validate token before making the call
  if (!token || token.trim() === '') {
    console.error('callApi: No token provided for API request');
    return {
      error: toAppError(new ApiError(401, 'No authentication token provided', null)),
    };
  }

  // Debug: Log request details for activity logs
  const isActivityLogsCall =
    apiCall.toString().includes('activity-logs') || args.toString().includes('organizationId');

  // Set the security data before making the call
  apiClient.setSecurityData(token);

  // Bind the API call to the client
  const boundApiCall = apiCall.bind(apiClient);

  try {
    const response = await boundApiCall(...args);

    const contentType = response.headers.get('content-type') ?? '';
    const parsedBody = contentType.includes('application/json')
      ? await response.json().catch(() => null)
      : await response.text().catch(() => null);

    if (!response.ok) {
      const errorMessage =
        typeof parsedBody === 'object'
          ? parsedBody?.error ||
            parsedBody?.message ||
            `Request failed with status ${response.status}`
          : `Request failed with status ${response.status}`;

      // Debug: Log error details for activity logs
      if (isActivityLogsCall) {
        console.error('🐛 callApi ERROR (Activity Logs):', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorMessage,
          parsedBody,
          headers: Object.fromEntries(response.headers.entries()),
        });
      }

      return {
        error: toAppError(new ApiError(response.status, errorMessage, parsedBody)),
      };
    }

    return { data: parsedBody as T };
  } catch (_error) {
    console.error('Error making API request:', _error);
    return { error: toAppError(_error) };
  }
}

export async function fetchOrganization(id: string, token: string) {
  return callApi<Organization, [string]>(apiClient.organizations.fetchOrgDetailed, token, id);
}

export async function updatePreventionDeadline(
  preventionId: string,
  orgId: string,
  newDate: Date,
  token: string,
) {
  return callApi<PreventionAssignment, [string, UpdatePreventionAssignmentDeadlineDto]>(
    apiClient.preventionAssignment.updateDeadline,
    token,
    orgId,
    {
      preventionAssignmentId: preventionId,
      deadline: newDate.toISOString(),
    },
  );
}

/**
 * Admin-only deadline override that bypasses all validation
 * Used for testing and time-travel scenarios in admin tools
 */
export async function adminDeadlineOverride(
  assignmentId: string,
  orgId: string,
  targetDeadline: Date,
  token: string,
) {
  return callApi<PreventionAssignment, [string, AdminDeadlineOverrideDto]>(
    apiClient.admin.adminDeadlineOverride,
    token,
    orgId,
    {
      assignmentId,
      targetDeadline: targetDeadline.toISOString(),
    },
  );
}

export async function updateEmployeeNote(
  employeeId: string,
  orgId: string,
  note: string,
  token: string,
): Promise<ApiResponse<Employee>> {
  return callApi<Employee, [string, string, UpdateEmployeeNoteDto]>(
    apiClient.humanResources.updateNoteForEmployee,
    token,
    orgId,
    employeeId,
    { note },
  );
}

export async function getOrganizationOptions(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetOrganizationOptionsData>> {
  return callApi<GetOrganizationOptionsData, [string]>(
    apiClient.humanResources.getOrganizationOptions,
    token,
    orgId,
  );
}

export async function getOrganizationOptionsKombo(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetOrganizationOptionsKomboData>> {
  return callApi<GetOrganizationOptionsKomboData, [string]>(
    apiClient.humanResources.getOrganizationOptionsKombo,
    token,
    orgId,
  );
}

export async function createManualEmployee(
  orgId: string,
  employeeData: ManualCreateEmployeeDto,
  token: string,
): Promise<ApiResponse<Employee>> {
  return callApi<Employee, [string, ManualCreateEmployeeDto]>(
    apiClient.humanResources.createEmployee,
    token,
    orgId,
    employeeData,
  );
}

export async function createLegalEntity(
  orgId: string,
  legalEntityData: CreateLegalEntityDto,
  token: string,
): Promise<ApiResponse<CreateLegalEntityData>> {
  return callApi<CreateLegalEntityData, [string, CreateLegalEntityDto]>(
    apiClient.humanResources.createLegalEntity,
    token,
    orgId,
    legalEntityData,
  );
}

export async function createWorkLocation(
  orgId: string,
  workLocationData: CreateWorkLocationDto,
  token: string,
): Promise<ApiResponse<CreateWorkLocationData>> {
  return callApi<CreateWorkLocationData, [string, CreateWorkLocationDto]>(
    apiClient.humanResources.createWorkLocation,
    token,
    orgId,
    workLocationData,
  );
}

export async function syncOrgKombo(orgId: string, token: string) {
  return callApi<ApiResponseDto, [string]>(apiClient.komboIntegration.syncById, token, orgId);
}

export async function processOrgKombo(orgId: string, token: string) {
  return callApi<ApiResponseDto, [string]>(apiClient.komboIntegration.processById, token, orgId);
}

export async function ingestOrgKombo(orgId: string, token: string) {
  return callApi<ApiResponseDto, [string]>(apiClient.komboIntegration.ingestById, token, orgId);
}

export async function evaluateRulesForOrg(orgId: string, token: string) {
  return callApi<void, [string]>(apiClient.preventionRegister.evaluateRulesForOrg, token, orgId);
}

export async function fetchActivityLogs(
  organizationId: string,
  token: string,
  aggregated: boolean,
  entityType?: EntityTypeEnum,
  entityTypes?: EntityTypeEnum[],
) {
  const params: GetAllLogsParams = {
    organizationId,
    entityType,
    aggregated,
  };

  // If entityTypes array is provided, convert to comma-separated string
  if (entityTypes && entityTypes.length > 0) {
    params.entityTypes = entityTypes;
  }

  return await callApi<GetAllLogsData, [GetAllLogsParams]>(
    apiClient.activityLogs.getAllLogs,
    token,
    params,
  );
}

export async function updateStatusForOrg(orgId: string, token: string) {
  return callApi<void, [string]>(apiClient.preventionRegister.updateStatusForOrg, token, orgId);
}

export async function fetchActivityLogsByEmployee(employeeId: string, token: string) {
  return callApi<GetLogsByEmployeeData, [string]>(
    apiClient.activityLogs.getLogsByEmployee,
    token,
    employeeId,
  );
}

export async function fetchActivityLogsByEntityId(
  entityId: string,
  organizationId: string,
  token: string,
) {
  const params: GetAllLogsParams = {
    organizationId,
    entityId,
    aggregated: false,
  };

  return callApi<GetAllLogsData, [GetAllLogsParams]>(
    apiClient.activityLogs.getAllLogs,
    token,
    params,
  );
}

export async function fetchPreventionActivityLogsForDetail(
  assignmentId: string,
  organizationId: string,
  employeeId: string | null,
  _preventionName: string | null,
  token: string,
): Promise<EnrichedActivityLog[]> {
  const { data, error } = await callApi<EnrichedActivityLog[], [GetPreventionDetailLogsParams]>(
    apiClient.activityLogs.getPreventionDetailLogs,
    token,
    {
      organizationId,
      employeeId: employeeId ?? '',
      assignmentId,
    },
  );

  if (error) {
    throw new Error(error.message || 'Failed to fetch prevention detail activity logs');
  }

  return data ?? [];
}

export async function getOhsHubOrgs(token: string) {
  return callApi<GetOhsHubOrgsData, []>(apiClient.organizations.getOhsHubOrgs, token);
}

export async function getOrgBudgets(token: string | null) {
  return callApi<GetOrgBudgetsData, []>(apiClient.admin.getOrgBudgets, token);
}

export async function updateUserOrganization(
  userId: string,
  token: string,
  orgId: string,
  setAsOhsHubAdmin?: boolean,
) {
  return callApi<UpdateOrganizationData, [string, UpdateUserOrganizationDto]>(
    apiClient.users.updateOrganization,
    token,
    userId,
    { organizationId: orgId, setAsOhsHubAdmin },
  );
}

export async function searchUsers(query: string, token: string) {
  return callApi<SearchUsersData, [SearchUsersParams, { secure: boolean }]>(
    apiClient.users.searchUsers,
    token,
    { query },
    { secure: true },
  );
}

export async function triggerDailySync(orgId: string, token: string) {
  return callApi<ApiResponseDto, [string]>(
    apiClient.komboIntegration.triggerDailySync,
    token,
    orgId,
  );
}

export async function processNotices(orgId: string, dryRun: boolean = true, token: string) {
  return callApi<ProcessNoticesForAllOrgsData, [ProcessNoticesForAllOrgsParams]>(
    apiClient.preventionRegister.processNoticesForAllOrgs,
    token,
    { orgId, dryRun },
  );
}

export async function processNoticesForSingleOrg(
  orgId: string,
  dryRun: boolean = true,
  token: string,
) {
  return callApi<ProcessNoticesForSingleOrgData, [ProcessNoticesForSingleOrgParams]>(
    apiClient.preventionRegister.processNoticesForSingleOrg,
    token,
    { orgId, dryRun },
  );
}

export async function clearUserCache(token: string) {
  return callApi<ClearUserCacheData, []>(apiClient.admin.clearUserCache, token);
}

export async function sendMessageToBooking(
  bookingId: string,
  messageDto: SessionMessageDto,
  token: string,
): Promise<ApiResponse<SessionMessage>> {
  return callApi<SessionMessage, [string, SessionMessageDto]>(
    apiClient.bookings.messages,
    token,
    bookingId,
    messageDto,
  );
}

export async function createFirstAidLog(
  orgId: string,
  logData: CreateFirstAidLogDto,
  token: string,
): Promise<ApiResponse<FirstAidLog>> {
  return callApi<FirstAidLog, [string, CreateFirstAidLogDto]>(
    apiClient.firstAidLogs.create,
    token,
    orgId,
    logData,
  );
}

export async function fetchFirstAidLogsByOrg(
  orgId: string,
  token: string,
): Promise<ApiResponse<FirstAidLog[]>> {
  return callApi<FirstAidLog[], [string]>(apiClient.firstAidLogs.findByOrganization, token, orgId);
}

export async function fetchFirstAidLogById(
  orgId: string,
  logId: string,
  token: string,
): Promise<ApiResponse<FirstAidLog>> {
  return callApi<FirstAidLog, [string, string]>(
    apiClient.firstAidLogs.findOne,
    token,
    orgId,
    logId,
  );
}

export async function updateFirstAidLogApi(
  orgId: string,
  logId: string,
  data: UpdateFirstAidLogDto,
  token: string,
): Promise<ApiResponse<FirstAidLog>> {
  return callApi(apiClient.firstAidLogs.update, token, logId, orgId, data);
}

export async function deleteFirstAidLog(
  orgId: string,
  logId: string,
  token: string,
): Promise<ApiResponse<void>> {
  return callApi<void, [string, string]>(apiClient.firstAidLogs.remove, token, orgId, logId);
}

/**
 * Public endpoint to fetch organization info by QR code
 * No authentication required - this is a public route
 */
export async function getOrganizationByQrCode(
  qrCode: string,
): Promise<ApiResponse<{ orgId: string; organizationName: string }>> {
  try {
    const response = await apiClient.organizations.getOrganizationByQrCode(qrCode);

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        error: toAppError(
          new ApiError(response.status, data.message || 'Failed to fetch organization', data),
        ),
      };
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    return {
      error: toAppError(
        new ApiError(
          500,
          error instanceof Error ? error.message : 'Failed to fetch organization',
          null,
        ),
      ),
    };
  }
}

/**
 * Public endpoint to submit external first aid log
 * No authentication required - this is a public route
 */
export async function submitExternalFirstAidLog(
  orgId: string,
  logData: CreateUnassignedFirstAidLogDto,
): Promise<ApiResponse<FirstAidLog>> {
  try {
    const response = await apiClient.organizations.submitExternalFirstAidLog(orgId, logData);

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return {
        error: toAppError(
          new ApiError(response.status, data.message || 'Failed to submit first aid log', data),
        ),
      };
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    return {
      error: toAppError(
        new ApiError(
          500,
          error instanceof Error ? error.message : 'Failed to submit first aid log',
          null,
        ),
      ),
    };
  }
}

export async function listAssessmentCycles(
  orgId: string,
  token: string,
): Promise<ApiResponse<AssessmentCycle[]>> {
  return callApi<AssessmentCycle[], [string]>(
    apiClient.mentalHealthRiskAssessments.listCycles,
    token,
    orgId,
  );
}

export async function getAssessmentCycle(
  orgId: string,
  cycleId: string,
  token: string,
): Promise<ApiResponse<AssessmentCycle>> {
  return callApi<AssessmentCycle, [string, string]>(
    apiClient.mentalHealthRiskAssessments.getCycle,
    token,
    orgId,
    cycleId,
  );
}

export async function createAssessmentCycle(
  orgId: string,
  data: CreateAssessmentCycleDto,
  token: string,
): Promise<ApiResponse<AssessmentCycle>> {
  return callApi<AssessmentCycle, [string, CreateAssessmentCycleDto]>(
    apiClient.mentalHealthRiskAssessments.createCycle,
    token,
    orgId,
    data,
  );
}

export async function updateAssessmentCycleNote(
  orgId: string,
  cycleId: string,
  data: UpdateAssessmentCycleNoteDto,
  token: string,
): Promise<ApiResponse<AssessmentCycle>> {
  return callApi<AssessmentCycle, [string, string, UpdateAssessmentCycleNoteDto]>(
    apiClient.mentalHealthRiskAssessments.updateCycleNote,
    token,
    orgId,
    cycleId,
    data,
  );
}

export async function closeAssessmentCycle(
  orgId: string,
  cycleId: string,
  token: string,
): Promise<ApiResponse<AssessmentCycle>> {
  return callApi<AssessmentCycle, [string, string]>(
    apiClient.mentalHealthRiskAssessments.closeCycle,
    token,
    orgId,
    cycleId,
  );
}

export async function finalizeAssessmentCycle(
  orgId: string,
  cycleId: string,
  token: string,
): Promise<ApiResponse<AssessmentCycle>> {
  return callApi<AssessmentCycle, [string, string]>(
    apiClient.mentalHealthRiskAssessments.finalizeCycle,
    token,
    orgId,
    cycleId,
  );
}

// Get survey for a specific assessment cycle
export async function getSurvey(
  token: string,
  locale: LocaleEnum,
): Promise<ApiResponse<SurveyResponseDto>> {
  return callApi<SurveyResponseDto, [{ token: string; locale?: LocaleEnum }]>(
    apiClient.public.getSurvey,
    token,
    { token, locale },
  );
}

// Document Management API functions
export async function requestDocumentUpload(
  orgId: string,
  uploadData: RequestUploadDto,
  token: string,
): Promise<ApiResponse<RequestUploadResponseDto>> {
  return callApi<RequestUploadResponseDto, [string, RequestUploadDto]>(
    apiClient.documents.requestUpload,
    token,
    orgId,
    uploadData,
  );
}

export async function completeDocumentUpload(
  orgId: string,
  documentId: string,
  token: string,
): Promise<ApiResponse<CompleteUploadData>> {
  return callApi<CompleteUploadData, [string, string]>(
    apiClient.documents.completeUpload,
    token,
    orgId,
    documentId,
  );
}

export async function getDocument(
  orgId: string,
  documentId: string,
  token: string,
): Promise<ApiResponse<DocumentDto>> {
  return callApi<DocumentDto, [string, string]>(
    apiClient.documents.getDocument,
    token,
    orgId,
    documentId,
  );
}

export async function getDocuments(
  orgId: string,
  filters: { entityType?: string; entityId?: string; uploaderId?: string },
  token: string,
): Promise<ApiResponse<DocumentDto[]>> {
  const params: GetDocumentsParams = {
    orgId,
    entityType: filters.entityType || '',
    entityId: filters.entityId || '',
    uploaderId: filters.uploaderId || '',
  };

  return callApi<DocumentDto[], [GetDocumentsParams]>(
    apiClient.documents.getDocuments,
    token,
    params,
  );
}

export async function deleteDocument(
  orgId: string,
  documentId: string,
  token: string,
): Promise<ApiResponse<DeleteDocumentData>> {
  return callApi<DeleteDocumentData, [string, string]>(
    apiClient.documents.deleteDocument,
    token,
    orgId,
    documentId,
  );
}

// Admin Prevention Management API functions
// Note: These are placeholder implementations using direct request method
// Once the backend endpoints are generated in the OpenAPI spec, replace with proper apiClient method calls

export async function listPreventionRules(
  orgId: string,
  token: string,
): Promise<ApiResponse<PreventionProfile[]>> {
  // Placeholder implementation - would use apiClient.admin.listPreventionRules once generated
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/rules/${orgId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();
  return response.ok
    ? { data }
    : {
        error: toAppError(
          new ApiError(response.status, data.message || 'Failed to fetch prevention rules', data),
        ),
      };
}

export async function createPreventionRule(
  ruleData: CreatePreventionRuleDto,
  token: string,
): Promise<ApiResponse<PreventionProfile>> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/rules`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ruleData),
  });

  const data = await response.json();
  return response.ok
    ? { data }
    : {
        error: toAppError(
          new ApiError(response.status, data.message || 'Failed to create prevention rule', data),
        ),
      };
}

export async function updatePreventionRule(
  id: string,
  ruleData: UpdatePreventionRuleDto,
  token: string,
): Promise<ApiResponse<PreventionProfile>> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/rules/${id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ruleData),
  });

  const data = await response.json();
  return response.ok
    ? { data }
    : {
        error: toAppError(
          new ApiError(response.status, data.message || 'Failed to update prevention rule', data),
        ),
      };
}

export async function deletePreventionRule(
  id: string,
  token: string,
): Promise<ApiResponse<{ message: string }>> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/rules/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();
  return response.ok
    ? { data }
    : {
        error: toAppError(
          new ApiError(response.status, data.message || 'Failed to delete prevention rule', data),
        ),
      };
}

export async function getEmployeesWithPreventionStatus(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetEmployeesWithPreventionStatusData>> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/employees/${orgId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();
  return response.ok
    ? { data }
    : {
        error: toAppError(
          new ApiError(response.status, data.message || 'Failed to fetch employees', data),
        ),
      };
}

export async function getPreventionTriggers(
  token: string,
): Promise<ApiResponse<{ id: string; name: string }[]>> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/triggers`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();
  return response.ok
    ? { data }
    : {
        error: toAppError(
          new ApiError(
            response.status,
            data.message || 'Failed to fetch prevention triggers',
            data,
          ),
        ),
      };
}

export async function getPreventionProfiles(
  orgId: string,
  token: string,
): Promise<ApiResponse<{ id: string; description: string; category: string }[]>> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/orgs/${orgId}/profiles`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const data = await response.json();
  return response.ok
    ? { data }
    : {
        error: toAppError(
          new ApiError(
            response.status,
            data.message || 'Failed to fetch prevention profiles',
            data,
          ),
        ),
      };
}

export async function fetchEmployeesWithPreventionStatus(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetEmployeesWithPreventionStatusData>> {
  return callApi<GetEmployeesWithPreventionStatusData, [string]>(
    apiClient.admin.getEmployeesWithPreventionStatus,
    token,
    orgId,
  );
}

/**
 * Fetch employees for an organization (admin use)
 * Returns employees with their associated user accounts
 */
export async function fetchEmployees(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetEmployeesWithPreventionStatusData>> {
  return callApi<GetEmployeesWithPreventionStatusData, [string]>(
    apiClient.admin.getEmployeesWithPreventionStatus,
    token,
    orgId,
  );
}

/**
 * Fetch users for an organization (admin use)
 * Returns all users with their roles and accessible entities/locations
 */
export async function fetchUsersByOrganization(
  orgId: string,
  token: string,
): Promise<ApiResponse<User[]>> {
  return callApi<User[], [string]>(apiClient.admin.getUsersByOrganization, token, orgId);
}

/**
 * Update user access including role, legal entities, and work locations
 */
export async function updateUserAccess(
  userId: string,
  dto: UpdateUserAccessDto,
  token: string,
): Promise<ApiResponse<{ message: string; user: User }>> {
  return callApi<{ message: string; user: User }, [string, UpdateUserAccessDto]>(
    apiClient.admin.updateUserAccess,
    token,
    userId,
    dto,
  );
}

export async function processAllPreventionEmailsUnified(
  orgId: string,
  dryRun: boolean,
  token: string,
): Promise<ApiResponse<ProcessAllPreventionEmailsUnifiedData>> {
  return callApi<ProcessAllPreventionEmailsUnifiedData, [ProcessAllPreventionEmailsUnifiedParams]>(
    apiClient.preventionRegister.processAllPreventionEmailsUnified,
    token,
    { orgId, dryRun },
  );
}

export async function processMandatoryEmailsForSingleOrg(
  orgId: string,
  dryRun: boolean,
  token: string,
): Promise<ApiResponse<ProcessMandatoryEmailsForSingleOrgData>> {
  return callApi<
    ProcessMandatoryEmailsForSingleOrgData,
    [ProcessMandatoryEmailsForSingleOrgParams]
  >(apiClient.preventionRegister.processMandatoryEmailsForSingleOrg, token, { orgId, dryRun });
}

/**
 * SECURE: Get signed download URL for medical-grade security
 * Returns a time-limited signed URL that expires in 2 minutes
 */
export async function getDocumentDownloadUrl(
  orgId: string,
  documentId: string,
  token: string,
): Promise<
  ApiResponse<{ downloadUrl: string; filename: string; mimeType: string; expiresIn: number }>
> {
  return callApi<
    { downloadUrl: string; filename: string; mimeType: string; expiresIn: number },
    [string, string]
  >(apiClient.documents.getDownloadUrl, token, orgId, documentId);
}

/**
 * LEGACY: Get direct stream URL (less secure)
 * Use getDocumentDownloadUrl instead for production/medical data
 */
export async function getDocumentStreamUrl(orgId: string, documentId: string): Promise<string> {
  // For direct streaming, we construct the URL
  // NOTE: This bypasses authentication headers in browser requests
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}/documents/orgs/${orgId}/documents/${documentId}/download`;
}

// Helper function to upload file directly to S3 using presigned URL
export async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<void> {
  console.log('uploadFileToS3: Starting upload to:', uploadUrl);
  console.log('uploadFileToS3: File details:', {
    name: file.name,
    type: file.type,
    size: file.size,
  });

  try {
    // Create an XMLHttpRequest for progress tracking if callback is provided
    if (onProgress && typeof XMLHttpRequest !== 'undefined') {
      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          console.log('uploadFileToS3: XHR Response received:', {
            status: xhr.status,
            statusText: xhr.statusText,
            responseHeaders: xhr.getAllResponseHeaders(),
          });

          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('uploadFileToS3: Upload successful via XHR');
            resolve();
          } else {
            console.error('uploadFileToS3: Upload failed via XHR:', {
              status: xhr.status,
              statusText: xhr.statusText,
              responseText: xhr.responseText,
            });

            if (xhr.status === 0 || xhr.status === 403) {
              reject(
                new Error(
                  `CORS or permission error: Unable to upload to S3. Status: ${xhr.status}`,
                ),
              );
            } else if (xhr.status >= 400 && xhr.status < 500) {
              reject(new Error(`Client error: ${xhr.statusText} (${xhr.status})`));
            } else if (xhr.status >= 500) {
              reject(new Error(`Server error: ${xhr.statusText} (${xhr.status})`));
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
            }
          }
        });

        xhr.addEventListener('error', (event) => {
          console.error('uploadFileToS3: XHR network error', {
            readyState: xhr.readyState,
            status: xhr.status,
            statusText: xhr.statusText,
            responseURL: xhr.responseURL,
            responseHeaders: xhr.getAllResponseHeaders(),
            event,
          });
          reject(
            new Error(
              `Network error: Unable to connect to S3. This is likely a CORS issue. The S3 bucket needs to allow requests from ${window.location.origin}`,
            ),
          );
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    }

    // Fallback to fetch API if no progress callback or XMLHttpRequest is not available
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
      // Add mode to handle potential CORS issues during development
      mode: 'cors',
    });

    console.log('uploadFileToS3: Response received:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No error text available');
      console.error('uploadFileToS3: Upload failed:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });

      // Provide more specific error messages based on status codes
      if (response.status === 0 || response.status === 403) {
        throw new Error(
          `CORS or permission error: Unable to upload to S3. Status: ${response.status}`,
        );
      } else if (response.status >= 400 && response.status < 500) {
        throw new Error(`Client error: ${response.statusText} (${response.status})`);
      } else if (response.status >= 500) {
        throw new Error(`Server error: ${response.statusText} (${response.status})`);
      } else {
        throw new Error(`Upload failed with status ${response.status}: ${response.statusText}`);
      }
    }

    console.log('uploadFileToS3: Upload successful');
  } catch (error) {
    console.error('uploadFileToS3: Exception during upload:', error);

    // Handle different types of errors
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error(
        'Network error: Unable to connect to S3. This might be a CORS issue or network connectivity problem.',
      );
    }

    // Re-throw the error if it's already our custom error
    throw error;
  }
}

export async function getSurveyGroups(token: string): Promise<ApiResponse<Group[]>> {
  return callApi<Group[], [string]>(apiClient.public.getGroups, token, token);
}

export async function submitSurveyResponse(
  token: string,
  responseData: SubmitAssessmentDto,
  orgId: string,
): Promise<ApiResponse<AssessmentResponse>> {
  return callApi<AssessmentResponse, [string, string, SubmitAssessmentDto]>(
    apiClient.public.submitResponse,
    token,
    token,
    orgId,
    responseData,
  );
}

// Maternity Risk Assessment API functions

// Maternity Notifications
export async function listMaternityNotifications(
  orgId: string,
  token: string,
  params?: { startDate?: string; endDate?: string },
): Promise<ApiResponse<MaternityNotificationResponseDto[]>> {
  return callApi<MaternityNotificationResponseDto[], [ListNotificationsParams]>(
    apiClient.maternityRiskAssessments.listNotifications,
    token,
    {
      orgId,
      ...params,
    },
  );
}

export async function getMaternityNotification(
  orgId: string,
  notificationId: string,
  token: string,
): Promise<ApiResponse<MaternityNotificationResponseDto>> {
  return callApi<MaternityNotificationResponseDto, [string, string]>(
    apiClient.maternityRiskAssessments.getNotification,
    token,
    orgId,
    notificationId,
  );
}

export async function createMaternityNotification(
  orgId: string,
  data: CreateMaternityNotificationDto,
  token: string,
): Promise<ApiResponse<MaternityNotificationResponseDto>> {
  return callApi<MaternityNotificationResponseDto, [string, CreateMaternityNotificationDto]>(
    apiClient.maternityRiskAssessments.createNotification,
    token,
    orgId,
    data,
  );
}

export async function updateMaternityNotification(
  orgId: string,
  notificationId: string,
  data: UpdateMaternityNotificationDto,
  token: string,
): Promise<ApiResponse<MaternityNotificationResponseDto>> {
  return callApi<
    MaternityNotificationResponseDto,
    [string, string, UpdateMaternityNotificationDto]
  >(apiClient.maternityRiskAssessments.updateNotification, token, orgId, notificationId, data);
}

export async function deleteMaternityNotification(
  orgId: string,
  notificationId: string,
  token: string,
): Promise<ApiResponse<void>> {
  return callApi<void, [string, string]>(
    apiClient.maternityRiskAssessments.deleteNotification,
    token,
    orgId,
    notificationId,
  );
}

export async function getNotificationsByEmployee(
  orgId: string,
  employeeId: string,
  token: string,
): Promise<ApiResponse<MaternityNotificationResponseDto[]>> {
  return callApi<MaternityNotificationResponseDto[], [string, string]>(
    apiClient.maternityRiskAssessments.getNotificationsByEmployee,
    token,
    orgId,
    employeeId,
  );
}

export async function getLatestNotificationByEmployee(
  orgId: string,
  employeeId: string,
  token: string,
): Promise<ApiResponse<MaternityNotificationResponseDto>> {
  return callApi<MaternityNotificationResponseDto, [string, string]>(
    apiClient.maternityRiskAssessments.getLatestNotificationByEmployee,
    token,
    orgId,
    employeeId,
  );
}

export async function getNotificationCounts(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetNotificationCountsData>> {
  return callApi<GetNotificationCountsData, [string]>(
    apiClient.maternityRiskAssessments.getNotificationCounts,
    token,
    orgId,
  );
}

// Maternity Risk Assessments
export async function listMaternityAssessments(
  orgId: string,
  token: string,
  params?: { completed?: boolean; status?: AssessmentStatus },
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto[]>> {
  return callApi<MaternityRiskAssessmentResponseDto[], [ListAssessmentsParams]>(
    apiClient.maternityRiskAssessments.listAssessments,
    token,
    {
      orgId,
      ...params,
    },
  );
}

export async function getMaternityAssessment(
  orgId: string,
  assessmentId: string,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto>> {
  return callApi<MaternityRiskAssessmentResponseDto, [string, string]>(
    apiClient.maternityRiskAssessments.getAssessment,
    token,
    orgId,
    assessmentId,
  );
}

export async function createMaternityAssessment(
  orgId: string,
  data: CreateMaternityRiskAssessmentDto,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto>> {
  return callApi<MaternityRiskAssessmentResponseDto, [string, CreateMaternityRiskAssessmentDto]>(
    apiClient.maternityRiskAssessments.createAssessment,
    token,
    orgId,
    data,
  );
}

export async function updateMaternityAssessment(
  orgId: string,
  assessmentId: string,
  data: UpdateMaternityRiskAssessmentDto,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto>> {
  return callApi<
    MaternityRiskAssessmentResponseDto,
    [string, string, UpdateMaternityRiskAssessmentDto]
  >(apiClient.maternityRiskAssessments.updateAssessment, token, orgId, assessmentId, data);
}

export async function deleteMaternityAssessment(
  orgId: string,
  assessmentId: string,
  token: string,
): Promise<ApiResponse<void>> {
  return callApi<void, [string, string]>(
    apiClient.maternityRiskAssessments.deleteAssessment,
    token,
    orgId,
    assessmentId,
  );
}

export async function getMaternityAssessmentWithDetails(
  orgId: string,
  assessmentId: string,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto>> {
  return callApi<MaternityRiskAssessmentResponseDto, [string, string]>(
    apiClient.maternityRiskAssessments.getAssessmentWithDetails,
    token,
    orgId,
    assessmentId,
  );
}

export async function updateMaternityAssessmentStatus(
  orgId: string,
  assessmentId: string,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto>> {
  return callApi<MaternityRiskAssessmentResponseDto, [string, string]>(
    apiClient.maternityRiskAssessments.updateAssessmentStatus,
    token,
    orgId,
    assessmentId,
  );
}

export async function getMaternityAssessmentsByEmployee(
  orgId: string,
  employeeId: string,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto[]>> {
  return callApi<MaternityRiskAssessmentResponseDto[], [string, string]>(
    apiClient.maternityRiskAssessments.getAssessmentsByEmployee,
    token,
    orgId,
    employeeId,
  );
}

export async function getMaternityAssessmentStatistics(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetAssessmentStatisticsData>> {
  return callApi<GetAssessmentStatisticsData, [string]>(
    apiClient.maternityRiskAssessments.getAssessmentStatistics,
    token,
    orgId,
  );
}

export async function getMaternityAssessmentTimeline(
  orgId: string,
  startDate: string,
  endDate: string,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto[]>> {
  return callApi<MaternityRiskAssessmentResponseDto[], [GetAssessmentTimelineParams]>(
    apiClient.maternityRiskAssessments.getAssessmentTimeline,
    token,
    {
      orgId,
      startDate,
      endDate,
    },
  );
}

// Questionnaire functions
export async function getMaternityQuestionnaire(
  orgId: string,
  token: string,
): Promise<ApiResponse<QuestionnaireResponseDto>> {
  return callApi<QuestionnaireResponseDto, [string]>(
    apiClient.maternityRiskAssessments.getQuestionnaire,
    token,
    orgId,
  );
}

export async function submitMaternityQuestionnaire(
  orgId: string,
  assessmentId: string,
  data: SubmitQuestionnaireResponseDto,
  token: string,
): Promise<ApiResponse<SubmitQuestionnaireData>> {
  return callApi<SubmitQuestionnaireData, [string, string, SubmitQuestionnaireResponseDto]>(
    apiClient.maternityRiskAssessments.submitQuestionnaire,
    token,
    orgId,
    assessmentId,
    data,
  );
}

export async function updateMaternityQuestionnaire(
  orgId: string,
  assessmentId: string,
  data: UpdateQuestionnaireResponseDto,
  token: string,
): Promise<ApiResponse<SubmitQuestionnaireData>> {
  return callApi<SubmitQuestionnaireData, [string, string, UpdateQuestionnaireResponseDto]>(
    apiClient.maternityRiskAssessments.updateQuestionnaire,
    token,
    orgId,
    assessmentId,
    data,
  );
}

export async function getMaternityQuestionnaireResponse(
  orgId: string,
  assessmentId: string,
  token: string,
): Promise<ApiResponse<GetQuestionnaireResponseData>> {
  return callApi<GetQuestionnaireResponseData, [string, string]>(
    apiClient.maternityRiskAssessments.getQuestionnaireResponse,
    token,
    orgId,
    assessmentId,
  );
}

// Employee Status
export async function getEmployeeMaternityStatus(
  orgId: string,
  employeeId: string,
  token: string,
): Promise<ApiResponse<GetEmployeeMaternityStatusData>> {
  return callApi<GetEmployeeMaternityStatusData, [string, string]>(
    apiClient.maternityRiskAssessments.getEmployeeMaternityStatus,
    token,
    orgId,
    employeeId,
  );
}

export async function updateAssessmentStatus(
  orgId: string,
  assessmentId: string,
  status: string,
  token: string,
): Promise<ApiResponse<MaternityRiskAssessmentResponseDto>> {
  return updateMaternityAssessmentStatus(orgId, assessmentId, token);
}

// Prevention Completion API functions
export async function getPreventionCompletionsByAssignment(
  orgId: string,
  assignmentId: string,
  token: string,
): Promise<ApiResponse<PreventionCompletion[]>> {
  return callApi<PreventionCompletion[], [string, string]>(
    apiClient.preventionCompletions.getCompletionsByAssignment,
    token,
    orgId,
    assignmentId,
  );
}

export async function getLatestPreventionCompletion(
  orgId: string,
  assignmentId: string,
  token: string,
): Promise<ApiResponse<PreventionCompletion | null>> {
  return callApi<PreventionCompletion | null, [string, string]>(
    apiClient.preventionCompletions.getLatestCompletion,
    token,
    orgId,
    assignmentId,
  );
}

export async function createPreventionCompletion(
  orgId: string,
  data: CreatePreventionCompletionDto,
  token: string,
): Promise<ApiResponse<PreventionCompletion>> {
  return callApi<PreventionCompletion, [string, CreatePreventionCompletionDto]>(
    apiClient.preventionCompletions.createCompletion,
    token,
    orgId,
    data,
  );
}

export async function updatePreventionCompletion(
  orgId: string,
  completionId: string,
  data: UpdatePreventionCompletionDto,
  token: string,
): Promise<ApiResponse<PreventionCompletion>> {
  return callApi<PreventionCompletion, [string, string, UpdatePreventionCompletionDto]>(
    apiClient.preventionCompletions.updateCompletion,
    token,
    orgId,
    completionId,
    data,
  );
}

export async function deletePreventionCompletion(
  orgId: string,
  completionId: string,
  token: string,
): Promise<ApiResponse<void>> {
  return callApi<void, [string, string]>(
    apiClient.preventionCompletions.deleteCompletion,
    token,
    orgId,
    completionId,
  );
}

// Note: Receipt uploads now use the document management system.
// Use PreventionCompletionService.uploadReceipt() instead.

export async function getPreventionReceiptSummary(
  orgId: string,
  completionId: string,
  token: string,
): Promise<ApiResponse<ReceiptSummaryResponseDto>> {
  return callApi<ReceiptSummaryResponseDto, [string, string]>(
    apiClient.preventionCompletions.getReceiptSummary,
    token,
    orgId,
    completionId,
  );
}

export async function getPreventionReceipt(
  orgId: string,
  completionId: string,
  token: string,
): Promise<ApiResponse<{ documentId: string; url: string }>> {
  return callApi<{ documentId: string; url: string }, [string, string]>(
    apiClient.preventionCompletions.getReceipt,
    token,
    orgId,
    completionId,
  );
}

export async function deletePreventionReceipt(
  orgId: string,
  completionId: string,
  token: string,
): Promise<ApiResponse<void>> {
  return callApi<void, [string, string]>(
    apiClient.preventionCompletions.deleteReceipt,
    token,
    orgId,
    completionId,
  );
}

export async function createPreventionWithProfile(
  data: CreatePreventionWithProfileDto,
  token: string,
): Promise<ApiResponse<CreatePreventionWithProfileResponseDto>> {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/admin/prevention/create-with-profile`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const responseData = await response.json();
  return response.ok
    ? { data: responseData }
    : {
        error: toAppError(
          new ApiError(
            response.status,
            responseData.message || 'Failed to create prevention profile',
            responseData,
          ),
        ),
      };
}

export type AdminInstructionTopic = InstructionTopicAdminResponseDto;
export type BootstrapInstructionTopicsResponse = BootstrapInstructionTopicsData;

export async function getInstructionTopicsForAdmin(
  orgId: string,
  token: string,
): Promise<ApiResponse<AdminInstructionTopic[]>> {
  return callApi<InstructionTopicAdminResponseDto[], [string]>(
    apiClient.admin.getInstructionTopics,
    token,
    orgId,
  );
}

export async function createInstructionWithProfile(
  data: CreateInstructionWithProfileDto,
  token: string,
): Promise<ApiResponse<CreateInstructionWithProfileResponseDto>> {
  return callApi<CreateInstructionWithProfileResponseDto, [CreateInstructionWithProfileDto]>(
    apiClient.admin.createInstructionWithProfile,
    token,
    data,
  );
}

export async function bootstrapInstructionTopics(
  orgId: string,
  token: string,
): Promise<ApiResponse<BootstrapInstructionTopicsResponse>> {
  return callApi<BootstrapInstructionTopicsData, [string]>(
    apiClient.admin.bootstrapInstructionTopics,
    token,
    orgId,
  );
}

export async function getKomboIntegration(
  orgId: string,
  token: string,
): Promise<ApiResponse<GetKomboIntegrationData | null>> {
  if (!orgId.trim()) {
    return { error: toAppError(new ApiError(400, 'Please enter an organization ID', null)) };
  }

  const response = await callApi<GetKomboIntegrationData, [string]>(
    apiClient.admin.getKomboIntegration,
    token,
    orgId.trim(),
  );

  if (response.error && response.error.status === 404) {
    return { data: null };
  }

  return response;
}

export async function createKomboIntegration(
  params: CreateKomboIntegrationDto,
  token: string,
): Promise<ApiResponse<CreateKomboIntegrationData>> {
  if (!params.orgId.trim()) {
    return { error: toAppError(new ApiError(400, 'Please enter an organization ID', null)) };
  }

  const requestData: CreateKomboIntegrationDto = {
    orgId: params.orgId.trim(),
    language:
      (params.language as CreateKomboIntegrationDtoLanguageEnum) ||
      CreateKomboIntegrationDtoLanguageEnum.De,
    enableFiltering: params.enableFiltering || false,
    enableFieldMapping: params.enableFieldMapping || false,
  };

  return callApi<CreateKomboIntegrationData, [CreateKomboIntegrationDto]>(
    apiClient.admin.createKomboIntegration,
    token,
    requestData,
  );
}

export async function updateOrgInstructionsAccess(
  data: UpdateOrgAccessDto,
  token: string,
): Promise<ApiResponse<UpdateInstructionsAccessData>> {
  return callApi<UpdateInstructionsAccessData, [UpdateOrgAccessDto]>(
    apiClient.admin.updateInstructionsAccess,
    token,
    data,
  );
}

export async function updateOrgEapAccess(
  data: UpdateOrgEapAccessDto,
  token: string,
): Promise<ApiResponse<UpdateEapAccessData>> {
  return callApi<UpdateEapAccessData, [UpdateOrgEapAccessDto]>(
    apiClient.admin.updateEapAccess,
    token,
    data,
  );
}

export async function updateOrgOhsHubAccess(
  data: UpdateOrgOhsHubAccessDto,
  token: string,
): Promise<ApiResponse<UpdateOhsHubAccessData>> {
  return callApi<UpdateOhsHubAccessData, [UpdateOrgOhsHubAccessDto]>(
    apiClient.admin.updateOhsHubAccess,
    token,
    data,
  );
}

export async function getAllOrgs(token: string): Promise<ApiResponse<GetAllOrgsData>> {
  return callApi<GetAllOrgsData, []>(apiClient.admin.getAllOrgs, token);
}

export async function fetchInstructionStatistics(
  orgId: string,
  token: string,
): Promise<ApiResponse<InstructionStatisticsResponseDto>> {
  return callApi<InstructionStatisticsResponseDto, [string]>(
    apiClient.statistics.getInstructionStatistics,
    token,
    orgId,
  );
}

export async function fetchInstructionAssignmentsByEmployee(
  orgId: string,
  employeeId: string | undefined,
  token: string,
): Promise<ApiResponse<InstructionAssignmentResponseDto[]>> {
  return callApi<InstructionAssignmentResponseDto[], [{ orgId: string; employeeId?: string }]>(
    apiClient.instructionAssignments.findAll,
    token,
    employeeId ? { orgId, employeeId } : { orgId },
  );
}

export async function updateInstructionDueDate(
  orgId: string,
  assignmentId: string,
  dueDate: string,
  token: string,
): Promise<ApiResponse<InstructionAssignmentResponseDto>> {
  return callApi<InstructionAssignmentResponseDto, [string, string, { dueDate: string }]>(
    apiClient.instructionAssignments.updateDueDate,
    token,
    orgId,
    assignmentId,
    { dueDate },
  );
}

export interface AdminExpertRow {
  id: string;
  fullName: string;
  email: string;
  calendlyUri: string | null;
  hasToken: boolean;
}

export interface UpdateCalendlyTokenResult {
  success: boolean;
  calendlyUriChanged: boolean;
  newCalendlyUri?: string;
}

export interface CreateAdminExpertResult {
  id: string;
  externalId: string;
  fullName: string;
  email: string;
  active: boolean;
  userId: string;
  clerkUserId: string;
}

export async function createAdminExpert(
  dto: CreateExpertDto,
  token: string,
): Promise<ApiResponse<CreateAdminExpertResult>> {
  return callApi<CreateAdminExpertResult, [CreateExpertDto]>(
    apiClient.admin.createExpert,
    token,
    dto,
  );
}

export async function listAdminExperts(token: string): Promise<ApiResponse<AdminExpertRow[]>> {
  return callApi<AdminExpertRow[], []>(apiClient.admin.listExperts, token);
}

export async function updateExpertCalendlyToken(
  expertId: string,
  calendlyAccessToken: string,
  token: string,
): Promise<ApiResponse<UpdateCalendlyTokenResult>> {
  return callApi<UpdateCalendlyTokenResult, [string, UpdateExpertCalendlyTokenDto]>(
    apiClient.admin.updateCalendlyToken,
    token,
    expertId,
    { calendlyAccessToken },
  );
}

// Export DTO types for components
export type {
  CreateExpertDto,
  CreatePreventionRuleDto,
  UpdatePreventionRuleDto,
  ReceiptSummaryResponseDto,
  CreatePreventionWithProfileDto,
  CreatePreventionWithProfileResponseDto,
  CreatePreventionWithProfileDtoAssignmentTypeEnum,
} from '@/types/Api';
